import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { NavigationHelperService } from '@shared/helpers/navigation.helpers.service';
import { PATHS } from '@shared/constants/paths';
import { filter, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { IMPORT_ORDERS_ACTIONS, Order } from '@usf/ngrx-order';
import { upsertMessage } from '@app/ngrx-message/store/actions/message/message.action';
import { Message } from '@app/ngrx-message/models/client/message';
import { v4 as uuidv4 } from 'uuid';
import { MessageTypeEnum } from '@app/ngrx-message/constants/messageTypeEnum';
import { productSelector } from '@shared/selectors/product.selectors';
import { OrderDownloadService } from '@shared/services/file-download/order-download.service';
import { Store } from '@ngrx/store';
import { ORDER_ACTIONS } from '@order/actions/order-actions';
import { ProductService } from '@shared/services/product/product.service';
import { combineLatest, of } from 'rxjs';
import { Product } from '@usf/product-types';

@Injectable({
  providedIn: 'root',
})
export class ImportOrderValidationClientEffects {
  importOrdersClientSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(IMPORT_ORDERS_ACTIONS.submitSuccess),
        tap(({ expectation }) =>
          this.navigationHelperService.routeTo(PATHS.IMPORTED_ORDERS),
        ),
      ),
    { dispatch: false },
  );

  importOrdersClientFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IMPORT_ORDERS_ACTIONS.submitFail),
      switchMap(error => [
        upsertMessage({
          message: {
            id: this.uuid(),
            stack: null,
            watermark: new Date().toUTCString(),
            read: false,
            type: MessageTypeEnum.error,
            toast: false,
            display:
              'There was an error submitting your imported order(s). Please close this modal and try again.',
            title: 'Try Again',
          } as Message,
        }),
      ]),
    ),
  );

  downloadImportedOrders$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ORDER_ACTIONS.downloadImportedOrders),
        mergeMap(payload => {
          const productNumbers = this.getProductNumbersFromOrders(
            payload?.orders,
          );
          this.productService.loadProducts(productNumbers);
          return combineLatest([
            of(payload),
            //Check if products are loaded
            this.store.select(productSelector).pipe(
              map((products: Map<number, Product>) => {
                let isLoaded = true;
                for (const productNumber of productNumbers) {
                  const product = products.get(productNumber);
                  if (!product) {
                    isLoaded = false;
                  }
                }
                return isLoaded;
              }),
            ),
            of(productNumbers),
          ]).pipe(
            filter(([payload, isLoaded]) => isLoaded),
            take(1),
          );
        }),
        mergeMap(([payload, isLoaded, productNumbers]) =>
          combineLatest([
            of(payload),
            this.productService.getProductsWithAlternatives(productNumbers),
          ]).pipe(take(1)),
        ),
        tap(([{ orders, fileName, fileType }, products]) => {
          this.orderDownloadService.downloadAndOpenOrder(orders, {
            fileName,
            fileType,
            products,
            multipleExports: true,
          });
        }),
      ),
    { dispatch: false },
  );

  uuid() {
    return uuidv4();
  }

  getProductNumbersFromOrders(orders: Order[]): number[] {
    const productNumbers = [];
    const productNumberMap = new Set();

    for (const order of orders) {
      if (order.orderItems) {
        for (const item of order.orderItems) {
          if (!productNumberMap.has(item.productNumber)) {
            productNumbers.push(item.productNumber);
            productNumberMap.add(item.productNumber);
          }
        }
      }
    }
    return productNumbers;
  }

  constructor(
    private actions$: Actions,
    private store: Store,
    private productService: ProductService,
    private orderDownloadService: OrderDownloadService,
    private navigationHelperService: NavigationHelperService,
  ) {}
}
